import type { VoidFunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { ControlLabel, FormControl, FormGroup, HelpBlock, InputGroup, Modal } from 'react-bootstrap';

import { window } from '../../../../globals';
import { userHasPermissionToCreatePolicyType } from '../../../../helpers/permissions';
import { useAsyncStatus } from '../../../../hooks';
import * as api from '../../../../redux/api';
import Button, { BUTTON_TYPE, ButtonGroup, InlineLoaderButton } from '../../../generic/Button';
import { LabeledCheckbox } from '../../../generic/Forms';
import Icon, { icons } from '../../../generic/Icon';
import { DocsLink, RouterLink } from '../../../generic/Link';
import QualityRequired from '../../../generic/Gates/QualityRequired';
import SecurityRequired from '../../../generic/Gates/SecurityRequired';
import { Organization, Token } from '../../../../types';
import { PolicyType } from '../../../../../typings/enums';

type ModalTokenSettingsProps = {
  token?: Token;
  isShowing: boolean;
  onHide: () => void;
} & Pick<Organization, 'policies'>;

type PolicyValue = number | undefined;

/**
 * @deprecated - Don't edit/add to this component if possible, as it has been replaced as part of GRANULAR_ORGANIZATION_SETTINGS and will soon be removed. See `app/componennts/settings/` for the new components.
 */
const ModalTokenSettings: VoidFunctionComponent<ModalTokenSettingsProps> = ({
  token,
  isShowing,
  onHide,
  policies,
}) => {
  const [newKeyName, setNewKeyName] = useState(token?.name || '');
  const [defaultPolicyId, setDefaultPolicy] = useState<PolicyValue>(token?.meta?.defaultPolicyId);
  const [defaultSecurityPolicyId, setDefaultSecurityPolicy] =
    useState<PolicyValue>(token?.meta?.defaultSecurityPolicyId);
  const [defaultQualityPolicyId, setDefaultQualityPolicy] = useState<PolicyValue>(token?.meta?.defaultQualityPolicyId);
  const [isPushOnly, setPushOnly] = useState(token?.meta?.pushOnly ?? true);

  const { status, onPending } = useAsyncStatus();

  useEffect(() => {
    setNewKeyName(token?.name || '');
    setDefaultPolicy(token?.meta?.defaultPolicyId);
    setDefaultSecurityPolicy(token?.meta?.defaultSecurityPolicyId);
    setDefaultQualityPolicy(token?.meta?.defaultQualityPolicyId);
    setPushOnly(token?.meta?.pushOnly ?? true);
  }, [token]);

  const policyOptionsOfType = useCallback((policyType: PolicyType) =>
    (policies || [])
      .filter((policy) => policy.type === policyType)
      .map((policy) => (
        <option key={policy.id} value={policy.id}>
          {policy.title}
        </option>
      )), [policies]);

  const createAPIToken = useCallback(async () => {
    if (!window.user) {
      return;
    }
    onPending();
    await api.post(`/api/user/${window.user.id}/api_token`, {
      name: newKeyName,
      pushOnly: isPushOnly.toString(),
    });
    window.location.reload();
  }, [newKeyName, isPushOnly, onPending]);

  const updateAPIToken = useCallback(async () => {
    if (!token) {
      return;
    }

    onPending();
    await api.put(`/api/user/${token.userId}/api_token/${token.id}`, {
      name: newKeyName,
      pushOnly: isPushOnly,
      meta: {
        defaultPolicyId,
        defaultSecurityPolicyId,
        defaultQualityPolicyId,
      },
    });
    window.location.reload();
  }, [
    token,
    defaultPolicyId,
    defaultSecurityPolicyId,
    defaultQualityPolicyId,
    isPushOnly,
    newKeyName,
    onPending,
  ]);

  const createOrUpdateToken = useCallback(() => {
    if (token) {
      updateAPIToken();
      return;
    }
    createAPIToken();
  }, [token, createAPIToken, updateAPIToken]);

  return (
    <Modal
      className="modal-horizontal"
      bsSize="large"
      show={isShowing}
      onHide={onHide}
    >
      <div className="row">
        <div className="col-md-3 modal-horizontal-header">
          <div className="padded">
            <span className="icon-feature">
              <Icon icon={icons.addToProject} />
            </span>
            <h4>{token ? 'Edit' : 'Create'} an API Token</h4>
          </div>
        </div>
        <div className="col-md-9 modal-horizontal-content">
          <div className="padded">
            <h3>Basic settings</h3>
            <FormGroup
              controlId="repoInputForm"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <InputGroup className="input-group input-group-md">
                <ControlLabel className="form-label">Name</ControlLabel>
                <FormControl
                  type="text"
                  bsSize="large"
                  className="input-repo-url input-block-level"
                  // @ts-expect-error This value does, in fact, exist
                  onChange={(e) => setNewKeyName(e.target.value)}
                  value={newKeyName}
                  placeholder="My API token name"
                />
              </InputGroup>
              <InputGroup
                className="input-group input-group-md"
                style={{ marginTop: 30 }}
              >
                <LabeledCheckbox
                  label="Push Only"
                  isChecked={isPushOnly}
                  onChange={() => {
                    setPushOnly(!isPushOnly);
                  }}
                  isDisabled={!!token}
                />
                <HelpBlock>
                  Push only tokens can only be used to send data to FOSSA.
                  Select this option if you only plan to use the API token with{' '}
                  <DocsLink to="provided-builds">Provided Builds</DocsLink>. Do{' '}
                  <strong>not</strong> select this option if you want to have
                  full access to the FOSSA API.{' '}
                  <strong>Once created, tokens cannot be changed.</strong>
                </HelpBlock>
              </InputGroup>
              {/*
              Show the policy selectors only when editing a policy because the backend doesn't support selecting
              policies when creating a token.
            */}
              {!!token && (
                <div>
                  <h3>
                    Select associated policy <small>(optional)</small>
                  </h3>
                  <HelpBlock>
                    Select the default policy that should be used for all new
                    projects created by fossa-cli using this API key. If a
                    policy is not set for this key, your organization default
                    will be used.
                  </HelpBlock>
                  <InputGroup
                    className="input-group input-group-md"
                    style={{ marginTop: 20 }}
                  >
                    <ControlLabel className="form-label">
                      Default Licensing Policy
                      {userHasPermissionToCreatePolicyType(PolicyType.LICENSING) && (
                        <RouterLink
                          to="/policies/licensing"
                          className="margin-left-xs"
                        >
                          (+ Create New)
                        </RouterLink>
                      )}
                    </ControlLabel>
                    <FormControl
                      componentClass="select"
                      name="default-license-policy"
                      value={defaultPolicyId}
                      onChange={(e) => {
                        // @ts-expect-error This value does, in fact, exist
                        setDefaultPolicy(e.target.value ? Number(e.target.value) : undefined);
                      }}
                    >
                      <option value="">--- Select a Policy ---</option>
                      {policyOptionsOfType(PolicyType.LICENSING)}
                    </FormControl>
                  </InputGroup>
                  <SecurityRequired>
                    <InputGroup
                      className="input-group input-group-md"
                      style={{ marginTop: 20 }}
                    >
                      <ControlLabel className="form-label">
                        Default Security Policy
                        {userHasPermissionToCreatePolicyType(PolicyType.SECURITY) && (
                          <RouterLink
                            to="/policies/security"
                            className="margin-left-xs"
                          >
                            (+ Create New)
                          </RouterLink>
                        )}
                      </ControlLabel>
                      <FormControl
                        componentClass="select"
                        name="default-security-policy"
                        value={defaultSecurityPolicyId}
                        onChange={(e) => {
                          // @ts-expect-error This value does, in fact, exist
                          setDefaultSecurityPolicy(e.target.value ? Number(e.target.value) : undefined);
                        }}
                        >
                        <option value="">--- Select a Policy ---</option>
                        {policyOptionsOfType(PolicyType.SECURITY)}
                      </FormControl>
                    </InputGroup>
                  </SecurityRequired>
                  <QualityRequired>
                    <InputGroup
                      className="input-group input-group-md"
                      style={{ marginTop: 20 }}
                    >
                      <ControlLabel className="form-label">
                        Default Quality Policy
                        {userHasPermissionToCreatePolicyType(PolicyType.QUALITY) && (
                          <RouterLink
                            to="/policies/quality"
                            className="margin-left-xs"
                          >
                            (+ Create New)
                          </RouterLink>
                        )}
                      </ControlLabel>
                      <FormControl
                        componentClass="select"
                        name="default-quality-policy"
                        value={defaultQualityPolicyId}
                        onChange={(e) => {
                          // @ts-expect-error This value does, in fact, exist
                          setDefaultQualityPolicy(e.target.value ? Number(e.target.value) : undefined);
                        }}
                        >
                        <option value="">--- Select a Policy ---</option>
                        {policyOptionsOfType(PolicyType.QUALITY)}
                      </FormControl>
                    </InputGroup>
                  </QualityRequired>
                </div>
              )}
            </FormGroup>
            <br />
            <ButtonGroup spacing="xs">
              <Button onClick={onHide} type={BUTTON_TYPE.SECONDARY}>Cancel</Button>&nbsp;&nbsp;
              <InlineLoaderButton
                onClick={createOrUpdateToken}
                isLoading={status.loading}
                isDisabled={status.loading}
              >
                {status.loading ? 'Saving' : 'Confirm'}
              </InlineLoaderButton>
            </ButtonGroup>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalTokenSettings;
